const { debounce, translateString } = window.BAO.utils
const { ProductCard } = window.BAO.Tagalys
class TagalysQuickSearch extends window.BAO.CustomElement() {
  static get requiredElements () {
    return ['input', 'body', 'popular-searches', 'no-results']
  }

  static get requiredExternalElements () {
    return ['trigger']
  }

  connectedCallback () {
    super.connectedCallback()

    this.setDefaults()
  }

  setupListeners () {
    super.setupListeners()

    if (!this.els?.input?.element) return

    this.handlePointerEvent = this.handlePointerEvent.bind(this)
    this.externalEls?.trigger?.elements?.forEach(triggerEl => {
      this.listeners.add(triggerEl, 'pointerdown', this.handlePointerEvent)
      this.listeners.add(triggerEl, 'pointerup', this.handlePointerEvent)
    })

    this.debouncedHandleInput = debounce(this.handleInput.bind(this), 250)
    this.listeners.add(
      this.els.input.element,
      'input',
      this.debouncedHandleInput.bind(this),
    )

    if (!window?.onTagalysReady) return

    // eslint-disable-next-line no-undef
    onTagalysReady(this.initialise.bind(this))
  }

  handlePointerEvent () {
    this.els.input.element.focus()
  }

  initialise () {
    // eslint-disable-next-line no-undef
    Tagalys.UIWidgets.SearchSuggestions.init(this.els.input.selector, {
      searchResultsURL: this.translations?.search_url,
      callbacks: {
        onSuccessfulAPICall: this.onSuccessfulAPICall.bind(this),
      },
      templates: {
        widget: {
          options: {
            includeTagalysCSS: false,
            selector: this.els.body.selector,
            request: {
              products: {
                limit: 10,
              },
              collections: {
                limit: 10,
              },
              pages: {
                limit: 10,
              },
              pages_blog: {
                limit: 10,
              },
            },
            position: false,
            minimumCharactersToShowSuggestions: this
              .minimumCharactersToShowSuggestions,
            maxRecentSearchesCount: 5,
            maxTotalSearchesCount: 10,
            alignToSelector: false,
            inheritAlignmentElementWidth: false,
          },
          render: this.renderSearchSuggestions.bind(this),
        },
        textSuggestionSection: {
          render: this.renderTextSuggestionSection.bind(this),
        },
        textSuggestionSectionItem: {
          render: this.renderTextSuggestionSectionItem.bind(this),
        },
        products: {
          render: this.renderProducts.bind(this),
        },
        productCard: {
          render: this.renderProductCard.bind(this),
        },
        header: {
          render: this.renderHeader.bind(this),
        },
      },
    })
  }

  onSuccessfulAPICall (response, helpers) {
    this.toggleDefaultContent(response, helpers)
  }

  handleInput (event) {
    const characterCount = event.target?.value ? event.target.value.length : 0

    if (characterCount < this.minimumCharactersToShowSuggestions) {
      this.setAttribute('state', 'default')
    }
  }

  toggleDefaultContent (response, helpers) {
    const searchTerm = helpers?.getEnteredSearchQuery()
    const hasProductResults = !!response?.products?.length
    const hasSearchTerm =
      searchTerm?.length >= this.minimumCharactersToShowSuggestions

    if (hasSearchTerm && hasProductResults) {
      // Search has produced results
      this.setAttribute('state', 'results')
    } else if (hasSearchTerm && !hasProductResults) {
      // Search hasn't produced results
      this.setAttribute('state', 'no-results')

      if (this?.els['no-results']?.element) {
        this.els['no-results'].element.textContent = translateString(
          this.translations?.quicksearch?.no_results_found,
          { term: `"${searchTerm}"` },
        )
      }
    } else {
      // No search - Show default state
      this.setAttribute('state', 'default')
    }
  }

  togglePopularSearches (args) {
    const { props } = args
    const { helpers } = props
    const { popularSearches } = helpers.getRecentAndPopularSearches(5, 10)

    if (!this.popularSearchesEl || popularSearches?.length < 5) return

    this.popularSearchesEl.innerHTML = /* html */ `
      <div class="hd-Search_Section hd-Search_Section-popular">
        <header class="hd-Search_Header">
          <h3 class="hd-Search_Heading fz-18_120">
            ${this.translations?.quicksearch?.headings?.popular_searches}
          </h3>
        </header>

        <div class="hd-Search_Content">
          <ul class="hd-Search_List">
            ${popularSearches
              .map(
                popularSearch => /* html */ `
                  <li class="hd-Search_Item">
                    <a
                      class="hd-Search_Link hd-Search_Link-popular"
                      href="${helpers.getLink(popularSearch.queryString)}"
                    >
                      ${popularSearch.displayString}
                    </a>
                  </li>
                `,
              )
              .join('')}
          </ul>
        </div>
      </div>
    `
  }

  renderSearchSuggestions (html, args) {
    const { templates, props } = args
    const { helpers } = props
    const searchTerm = helpers?.getEnteredSearchQuery()
    const textSuggestions = helpers.getTextSuggestions()
    const hasSearchTerm =
      searchTerm?.length >= this.minimumCharactersToShowSuggestions

    this.togglePopularSearches(args)

    if (hasSearchTerm) {
      return html`
        <div class="hd-Search_Suggestions hd-Search_Suggestions-results">
          <div class="hd-Search_Columns">
            <div class="hd-Search_Column hd-Search_Column-aside">
              ${helpers.hasTextSearchSuggestions()
                ? textSuggestions.map(
                    textSuggestionSection =>
                      html`
                        <${templates.textSuggestionSection}
                          textSuggestionSection=${textSuggestionSection}
                        />
                      `,
                  )
                : html`
                    <tagalys-quick-search-text-suggestions></tagalys-quick-search-text-suggestions>
                  `}
            </div>

            <div class="hd-Search_Column hd-Search_Column-main">
              <${templates.products} />
            </div>
          </div>
        </div>
      `
    }

    return ''
  }

  renderTextSuggestionSection (html, args) {
    const { templates } = args
    const { textSuggestionSection } = args.props

    return html`
      <div
        class="hd-Search_Section hd-Search_Section-${textSuggestionSection.section_id}"
        data-section-id=${textSuggestionSection.section_id}
      >
        <${templates.header}
          title=${translateString(
            this.translations?.quicksearch?.headings?.suggested_type,
            {
              type: textSuggestionSection.section_title,
            },
          )}
        />

        <div class="hd-Search_Content">
          <ul class="hd-Search_List">
            ${textSuggestionSection.items.map(textSuggestionSectionItem => {
              return html`
                <${templates.textSuggestionSectionItem}
                  textSuggestionSectionId=${textSuggestionSection.section_id}
                  textSuggestionSectionItem=${textSuggestionSectionItem}
                />
              `
            })}
          </ul>
        </div>
      </div>
    `
  }

  renderTextSuggestionSectionItem (html, args) {
    const { textSuggestionSectionItem, helpers } = args.props
    const url = textSuggestionSectionItem?.link
      ? textSuggestionSectionItem.link
      : helpers.getLink(textSuggestionSectionItem.queryString)

    return html`
      <li class="hd-Search_Item">
        <a class="hd-Search_Link hd-Search_Link-collection" href=${url}>
          ${textSuggestionSectionItem.displayString}
        </a>
      </li>
    `
  }

  renderProducts (html, args) {
    const { props, templates } = args
    const { helpers } = props
    const products = helpers.getProducts()

    return html`
      <div class="hd-Search_Section hd-Search_Section-products">
        <${templates.header}
          title=${this.translations?.quicksearch?.headings?.product_results}
          linkToAllResults="true"
        />

        <div class="hd-Search_Content">
          <div class="hd-Search_Products">
            ${products.map((product, index) => {
              return html`
                <div class="hd-Search_Product">
                  <${templates.productCard}
                    key=${index}
                    product=${product}
                    classModifier="quickSearch"
                    mobileViewport="45vw"
                    desktopViewport="205px"
                  />
                </div>
              `
            })}
          </div>
        </div>
      </div>
    `
  }

  renderProductCard (html, args) {
    return new ProductCard(html, args, this.translations)
  }

  renderHeader (html, args) {
    const { helpers, title, linkToAllResults } = args.props
    const searchParams =
      linkToAllResults === 'true'
        ? new URLSearchParams({ q: helpers.getEnteredSearchQuery() })
        : null

    return html`
      <header class="hd-Search_Header">
        <h3 class="hd-Search_Heading fz-18_120">
          ${title}
        </h3>

        ${searchParams
          ? html`
              <a
                class="hd-Search_Button fz-15_140"
                href="${helpers.getLink(searchParams.toString())}"
                aria-label="${translateString(
                  this.translations?.quicksearch?.view_all_results,
                  { term: helpers.getEnteredSearchQuery() },
                )}"
              >
                ${this.translations?.quicksearch?.view_all}
              </a>
            `
          : ''}
      </header>
    `
  }

  setDefaults () {
    this.defaultPopularSearchesHTML = this.popularSearchesEl
      ? this.popularSearchesEl.innerHTML
      : ''
    this.popularSearchesHTML = this.defaultPopularSearchesHTML
  }

  get popularSearchesEl () {
    return this.els?.['popular-searches']?.element
  }

  get minimumCharactersToShowSuggestions () {
    return 3
  }

  get translations () {
    return window.theme.strings
  }
}

if (!customElements.get('tagalys-quick-search')) {
  customElements.define('tagalys-quick-search', TagalysQuickSearch)
}

class TagalysQuickSearchTextSuggestions extends window.BAO.CustomElement() {
  connectedCallback () {
    super.connectedCallback()
    if (!this.tagalysQuickSearchEl?.popularSearchesHTML) return
    this.innerHTML = this.tagalysQuickSearchEl.popularSearchesHTML
  }

  get tagalysQuickSearchEl () {
    return this.closest('tagalys-quick-search')
  }
}

if (!customElements.get('tagalys-quick-search-text-suggestions')) {
  customElements.define(
    'tagalys-quick-search-text-suggestions',
    TagalysQuickSearchTextSuggestions,
  )
}
